import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import {
    getCustomListCatalogItem,
    getCustomListCatalogItemId,
} from 'entities/CustomList';

import type { AppDispatch } from 'app/providers/StoreProvider';
import type {
    MappedCatalogItem,
    NewlyAddedItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { CustomListItem } from 'entities/CustomList';
import type { CatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';

interface ListItemSelectionRef {
    selectionKey: string;
    catalogItemId?: string;
    targetQuantity: number;
    title?: string;
    propertyCategoryId?: string;
    itemUid: string;
}

const mapCustomListItemToSelectionRef = (
    listItem: CustomListItem,
    identity: CatalogIdentity,
): ListItemSelectionRef | null => {
    const catalogItem = getCustomListCatalogItem(listItem);
    if (!catalogItem) {
        return null;
    }

    const selectionKey = getItemUid(catalogItem, identity);
    const catalogItemId = getCustomListCatalogItemId(listItem);
    const targetQuantity = listItem.targetQuantity || 1;

    return {
        selectionKey,
        catalogItemId,
        targetQuantity,
        title: catalogItem.title,
        propertyCategoryId: catalogItem.categoryId,
        itemUid: selectionKey,
    };
};

export const stampCustomListOnNewAddedItems = (
    dispatch: AppDispatch,
    listId: string,
    listItems: CustomListItem[],
    identity: CatalogIdentity,
    newAddedItems: Record<string, NewlyAddedItem> | undefined,
) => {
    listItems.forEach((listItem) => {
        const ref = mapCustomListItemToSelectionRef(listItem, identity);
        if (!ref) {
            return;
        }

        const existingNewItem = newAddedItems?.[ref.selectionKey];
        dispatch(
            catalogItemActions.addNewItem({
                itemId: ref.selectionKey,
                source: existingNewItem?.source ?? 'lists',
                categoryId:
                    existingNewItem?.categoryId ?? ref.propertyCategoryId,
                listId,
            }),
        );
    });
};

/**
 * Stages list items into catalog selection (new-order path).
 * Overlapping keys sum target onto existing qty (P6-16).
 */
export const stageCustomListItemsInSelection = (
    dispatch: AppDispatch,
    listItems: CustomListItem[],
    identity: CatalogIdentity,
    selectedItems: Record<string, Partial<MappedCatalogItem>> | undefined,
    newAddedItems: Record<string, NewlyAddedItem> | undefined,
    listId: string,
) => {
    listItems.forEach((listItem) => {
        const ref = mapCustomListItemToSelectionRef(listItem, identity);
        if (!ref) {
            return;
        }

        const existing = selectedItems?.[ref.selectionKey];
        const existingQty = existing?.qty ?? 0;

        if (!existing) {
            dispatch(
                catalogItemActions.addMultiselectItem({
                    id: ref.selectionKey,
                    uid: ref.itemUid,
                    catalogItemId: ref.catalogItemId,
                    title: ref.title,
                    propertyCategoryId: ref.propertyCategoryId,
                }),
            );
            dispatch(
                catalogItemActions.setItemQuantity({
                    id: ref.selectionKey,
                    value: ref.targetQuantity,
                }),
            );
        } else {
            dispatch(
                catalogItemActions.setItemQuantity({
                    id: ref.selectionKey,
                    value: existingQty + ref.targetQuantity,
                }),
            );
        }
    });

    stampCustomListOnNewAddedItems(
        dispatch,
        listId,
        listItems,
        identity,
        newAddedItems,
    );
};
